import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Badge, InputGroup } from "react-bootstrap";
import Message from "../../../Components/Message";
import apiClient from '../../../AppClient';
import { FaSearch, FaTimes } from "react-icons/fa";
import axios from "axios";

const FertilizationModal = ({
  show,
  handleClose,
  mode,
  fertilizationData,
  fertilizers,
  products,
  handleSubmit
}) => {
  const [error, setError] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [formData, setFormData] = useState({
    fertilizer: "",
    products: [],
    amount: "",
    employee_name: "",
    notes: ""
  });

  const [searchTerm, setSearchTerm] = useState("");

  // Fetch staff data when modal opens
  useEffect(() => {
    if (show) {
      fetchStaff();
    }
  }, [show]);

  const fetchStaff = async () => {
    try {
      const response = await apiClient.get('/staff/');
      console.log('Staff API response:', response.data); // Add this line
      setStaff(response.data.results || response.data.staff || response.data.data);
      setError(null);
    } catch (error) {
      if (error.response) {
        setError(error.response.data.detail);
      } else {
        setError('فشل في تحميل بيانات الموظفين');
      }
    }
  };
  
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.subCategory.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (fertilizationData) {
      setFormData({
        ...fertilizationData,
        products: fertilizationData.products.map(p => p.id.toString())
      });
    } else {
      setFormData({
        fertilizer: "",
        products: [],
        amount: "",
        employee_name: "",
        notes: ""
      });
    }
  }, [fertilizationData, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.fertilizer) {
      setError("يرجى اختيار السماد");
      return false;
    }
    if (formData.products.length === 0) {
      setError("يرجى اختيار منتج واحد على الأقل");
      return false;
    }
    if (!formData.amount || isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      setError("يرجى إدخال كمية صحيحة");
      return false;
    }
    if (!formData.employee_name) {
      setError("يرجى إدخال اسم الموظف");
      return false;
    }
    return true;
  };

  const onSubmit = () => {
    if (validateForm()) {
      // Find the selected fertilizer to update its amount
      const selectedFertilizer = fertilizers.find(f => f.id.toString() === formData.fertilizer);
      
      if (selectedFertilizer) {
        const updatedFertilizers = fertilizers.map(f => {
          if (f.id.toString() === formData.fertilizer) {
            return {
              ...f,
              amount: parseFloat(f.amount) - parseFloat(formData.amount)
            };
          }
          return f;
        });
        
        // Pass the formData directly, not nested
        handleSubmit(formData);
      } else {
        handleSubmit(formData);
      }
      
      setError(null);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold">
          {mode === "add" ? "إضافة عملية تسميد جديدة" : "تعديل عملية التسميد"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {error && <Message variant="danger" onClose={() => setError(null)}>{error}</Message>}
        
        <Form>
          {/* Fertilizer Dropdown */}
          <Form.Group className="mb-4" controlId="fertilizerSelect">
            <Form.Label className="fw-bold">السماد</Form.Label>
            <Form.Select
              name="fertilizer"
              value={formData.fertilizer}
              onChange={handleChange}
              required
              className="border-2"
            >
              <option value="">اختر السماد</option>
              {fertilizers.map(fertilizer => (
                <option key={fertilizer.id} value={fertilizer.id}>
                  {fertilizer.name} (المتاح: {fertilizer.amount} كجم)
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Products Selection */}
          <Form.Group className="mb-4" controlId="productsCheckbox">
            <Form.Label className="fw-bold">المنتجات المختارة</Form.Label>
            
            {/* Selected Products Badges */}
            {formData.products.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-3">
                {formData.products.map(productId => {
                  const product = products.find(p => p.id.toString() === productId);
                  return product ? (
                    <Badge key={productId} bg="success" className="d-flex align-items-center">
                      {product.name} (المتاح: {product.amount})
                      <button 
                        type="button" 
                        className="btn-close btn-close-white ms-2" 
                        style={{fontSize: '0.5rem'}}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            products: prev.products.filter(id => id !== productId)
                          }));
                        }}
                      />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            
            {/* Search Input */}
            <InputGroup className="mb-3">
              <InputGroup.Text className="bg-white">
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="ابحث عن منتج..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-start-0"
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={clearSearch}
                  title="مسح البحث"
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
            
            {/* Products List */}
            <div className="border rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {filteredProducts.length > 0 ? (
                <div className="list-group list-group-flush">
                  {filteredProducts.map(product => (
                    <div 
                      key={product.id} 
                      className={`list-group-item list-group-item-action ${formData.products.includes(product.id.toString()) ? 'active' : ''}`}
                      onClick={() => {
                        setFormData(prev => {
                          const isSelected = prev.products.includes(product.id.toString());
                          const updatedProducts = isSelected
                            ? prev.products.filter(id => id !== product.id.toString())
                            : [...prev.products, product.id.toString()];
                          return { ...prev, products: updatedProducts };
                        });
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span className="fw-bold">{product.name}</span>
                          <small className="text-muted ms-2">
                            ({product.category.name} - {product.category.subCategory.name})
                          </small>
                          <small className="text-primary ms-2">
                            {/* (المتاح: {product.amount}) */}
                          </small>
                        </div>
                        <Form.Check 
                          type="checkbox"
                          checked={formData.products.includes(product.id.toString())}
                          onChange={() => {}}
                          className="ms-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-3 text-center text-muted">لا توجد نتائج مطابقة</div>
              )}
            </div>
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              {/* Amount Input */}
              <Form.Group className="mb-4" controlId="amount">
                <Form.Label className="fw-bold">الكمية (كجم)</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    required
                    className="text-end"
                  />
                  <InputGroup.Text>كجم</InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </div>
            <div className="col-md-6">
              {/* Employee Name Dropdown */}
              <Form.Group className="mb-4" controlId="employeeName">
                <Form.Label className="fw-bold">اسم الموظف</Form.Label>
                <Form.Select
                  name="employee_name"
                  value={formData.employee_name}
                  onChange={handleChange}
                  required
                  disabled={loadingStaff}
                >
                  <option value="">اختر الموظف</option>
                  {staff.map(employee => (
                    <option key={employee.id} value={employee.name}>
                      {employee.name}
                    </option>
                  ))}
                </Form.Select>
                {loadingStaff && <small className="text-muted">جاري تحميل بيانات الموظفين...</small>}
              </Form.Group>
            </div>
          </div>

          {/* Notes Input */}
          <Form.Group className="mb-3" controlId="notes">
            <Form.Label className="fw-bold">ملاحظات (اختياري)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="ملاحظات إضافية..."
              className="border-2"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <Button 
          variant="outline-secondary" 
          onClick={handleClose}
          className="px-4"
        >
          إلغاء
        </Button>
        <Button 
          variant="success" 
          onClick={onSubmit}
          className="px-4"
        >
          {mode === "add" ? "إضافة عملية التسميد" : "حفظ التعديلات"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FertilizationModal;